import clsx from 'clsx';
import React, { PropsWithChildren } from 'react';
import { cn } from '../../utils';

type Variant =
  | 'orange'
  | 'green'
  | 'blue'
  | 'subtle'
  | 'red'
  | 'disabled'
  | 'default'
  | 'peach'
  | 'gray';

type Size = 'small' | 'medium' | 'large';
export interface CustomChip {
  className?: string;
  variant?: never;
}
export interface VariantChip {
  variant?: Variant;
  className?: never;
}
type Props = (CustomChip | VariantChip) & {
  size?: Size;
  icon?: React.ReactNode;
  testId?: string;
};

export const Chip = ({
  icon,
  variant,
  size = 'medium',
  children,
  testId,
  className,
}: PropsWithChildren<Props>) => {
  return (
    <div
      className={clsx(
        'inline-flex items-center rounded-full leading-tight',
        size === 'large' ? 'text-base font-bold' : 'text-sm font-medium',
        icon && 'gap-1',
        {
          'bg-orange-background text-orange-brown': variant === 'orange',
          'bg-blue-100 text-blue-700': variant === 'blue',
          'bg-green-200 text-green-600': variant === 'green',
          'bg-red-50 text-red-600': variant === 'red',
          'bg-gray-500 text-white': variant === 'disabled',
          'bg-background text-primary': variant === 'subtle',
          'text-orange-brown bg-peach-100': variant === 'peach',
          'bg-gray-50 text-gray-700': variant === 'gray',
          'bg-componentBackgroundDarker text-primary':
            variant === 'default' || (!variant && !className),
        },
        {
          'py-0.5 px-2': !icon && size === 'small',
          'py-0.5 pr-2 pl-1': icon && size === 'small',
          'py-1 px-2': !icon && size === 'medium',
          'py-1 pr-3 pl-2': (icon && size === 'medium') || (icon && size === 'large'),
          'py-1 px-3': !icon && size === 'large',
        },
        className
      )}
      data-test-id={testId}
    >
      {icon && (
        <span
          className={cn(
            {
              'text-[1rem]': size === 'small',
              'text-[1.25rem]': size === 'medium',
              'text-[1.5rem]': size === 'large',
            },
            className
          )}
        >
          {icon}
        </span>
      )}
      {children}
    </div>
  );
};
